import { Button, ButtonGroup } from '@mui/material';
import { useMemo, useState } from 'react';
import KPIsTab from './KPIsTab';
import GraphsTab from './GraphsTab';
import { type HealthScore } from 'types/squares.type';
import { useAppSelector } from 'store/hook';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { productionLabels } from 'utils/helpers/labels';
import { calculateSavings } from '../hooks/useKPI';
import { calculateMaintenanceData } from '../utils/utils';
import { formatToUSD } from 'views/SurvivalAnalysis/Helper/Function/helper.function';
import dayjs from 'dayjs';
import { convertKmsToMiles } from 'utils/helpers/unitSystem';
import { useTranslation } from 'react-i18next';

interface KPIsGraphsProps {
  assetsItems: HealthScore[];
  setShowView: (view: string) => void;
  selectedStartDate: Date;
  selectedEndDate: Date;
}

export interface AssetCostData {
  total_cost: number;
  total_labour_cost: number;
  total_part_cost: number;
  total_tonnage: number;
  total_distance_kms: number;
  total_engine_duration_mins: number;
}

const KPIsGraphs = ({
  assetsItems,
  setShowView,
  selectedEndDate,
  selectedStartDate,
}: KPIsGraphsProps) => {
  const { t } = useTranslation();
  const { unitSystem } = useCustomerInfo();
  const squaresDashboardData = useAppSelector(
    (state) => state.squaresDashboardReducer.squares
  );
  const squaresDashboardAssetsData = useAppSelector(
    (state) => state.squaresDashboardReducer.assetDashboard
  );

  const productionUnitSystemLabel =
    unitSystem === 'metric'
      ? productionLabels.Load.metric
      : productionLabels.Load.imperial;

  const currDownTimeData = useAppSelector(
    (state) => state.squaresDashboardReducer?.downTimeData?.current
  );

  const [activeTab, setActiveTab] = useState('KPIs');
  const handleButtonClick = (tab: string) => {
    setActiveTab(tab);
  };

  const handleKpiCardClick = (cardType: string) => {
    if (
      cardType === 'assetsView' ||
      cardType === 'totalMaintenanceCost' ||
      cardType === 'partsReplacement' ||
      cardType === 'workOrderCount' ||
      cardType === 'maintenanceHours'
    ) {
      setShowView(cardType); // Set the view based on the card clicked
    }
  };

  // scheduled is pm and unscheduled
  const totalCurrentPmVsCmHrs = useMemo(() => {
    if (!currDownTimeData?.length) return { pm: 0, cm: 0 };
    const totals = currDownTimeData.reduce(
      (acc, item) => {
        acc.pm += item.pm_duration || 0;
        acc.cm += item.cm_duration || 0;
        return acc;
      },
      { pm: 0, cm: 0 }
    );
    return {
      pm: parseFloat((totals.pm / 3600).toFixed(2)),
      cm: parseFloat((totals.cm / 3600).toFixed(2)),
    };
  }, [currDownTimeData]);

  const aggregateAssetData = useMemo(() => {
    const temp: AssetCostData[] = Object.values(
      squaresDashboardAssetsData.assets || {}
    );

    console.log(temp);

    if (temp.length === 0) {
      return {
        total_cost: 0,
        total_labour_cost: 0,
        total_part_cost: 0,
        totalTonnage: 0,
        totalEngineHours: 0,
        totalMiles: 0,
      };
    }
    const aggregatedCosts = temp.reduce(
      (acc, asset) => {
        const totalCost = Number(asset.total_cost);
        const labourCost = Number(asset.total_labour_cost);
        const partCost = Number(asset.total_part_cost);
        acc.total_cost += !isNaN(totalCost) ? totalCost : 0;
        acc.total_labour_cost += !isNaN(labourCost) ? labourCost : 0;
        acc.total_part_cost += !isNaN(partCost) ? partCost : 0;

        // other kpi's
        const tonnage = Number(asset.total_tonnage) || 0;
        const durationMins = Number(asset.total_engine_duration_mins) || 0;
        const kms = Number(asset.total_distance_kms) || 0;

        acc.totalTonnage += isNaN(tonnage) ? 0 : tonnage;
        acc.totalEngineHours += isNaN(durationMins) ? 0 : durationMins / 60;
        acc.totalMiles += isNaN(kms) ? 0 : convertKmsToMiles(kms);

        return acc;
      },
      {
        total_cost: 0,
        total_labour_cost: 0,
        total_part_cost: 0,
        totalTonnage: 0,
        totalEngineHours: 0,
        totalMiles: 0,
      }
    );
    return aggregatedCosts;
  }, [squaresDashboardAssetsData]);

  const maintenanceData = useMemo(() => {
    const temp = calculateMaintenanceData({ data: squaresDashboardData });
    return temp;
  }, [squaresDashboardData]);

  const savings = useMemo(() => {
    return calculateSavings({
      totalWorkOrders: maintenanceData.totalMaintenanceQuantity,
      numAssets: assetsItems.length,
      maintenanceHoursPerWO: 24,
      manufacturerFrequency: 121,
      applicationFrequency: 136,
      totalMaintenanceCost: aggregateAssetData?.total_cost,
      tonsPerRound: 82.3,
      roundsPerDay: 20,
      startDate: dayjs(selectedStartDate).format('YYYY-MM-DD'),
      endDate: dayjs(selectedEndDate).format('YYYY-MM-DD'),
    });
  }, [
    maintenanceData,
    assetsItems.length,
    aggregateAssetData?.total_cost,
    selectedStartDate,
    selectedEndDate,
  ]);

  const totalDowntimeSaved = useMemo(() => {
    return [
      {
        header: '',
        value: (
          savings?.dateRange?.downtimeSaved ?? savings?.annual?.downtimeSaved
        )?.toFixed(2),
      },
    ];
  }, [savings]);

  const totalCostSaved = useMemo(() => {
    return [
      {
        header: '',
        value: `$${formatToUSD(
          savings?.dateRange?.costSaved ?? savings?.annual?.costSaved,
          'USD'
        )}`,
      },
    ];
  }, [savings]);

  const totalProductivityImproved = useMemo(() => {
    return [
      {
        header: '',
        value: formatToUSD(
          savings?.dateRange?.productivityImproved ??
            savings?.annual?.productivityImproved,
          productionUnitSystemLabel
        ),
      },
    ];
  }, [savings, productionUnitSystemLabel]);

  const totalTonnageOfAllAssets = [
    {
      header: '',
      value: aggregateAssetData.totalTonnage
        ? `${formatToUSD(
            aggregateAssetData.totalTonnage,
            productionUnitSystemLabel
          )}`
        : '0',
    },
  ];

  return (
    <div>
      <ButtonGroup
        color="secondary"
        aria-label={t('medium_sized_button_group')}
        fullWidth
      >
        <Button
          key="KPIs"
          onClick={() => {
            handleButtonClick('KPIs');
          }}
          sx={{
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            borderBottom: activeTab === 'KPIs' ? '2px solid #1976d2' : 'none',
            color: activeTab === 'KPIs' ? '#1976d2' : 'grey',
          }}
        >
          KPIs
        </Button>
        <Button
          key="Graphs"
          onClick={() => {
            handleButtonClick('Graphs');
          }}
          sx={{
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            borderBottom: activeTab === 'Graphs' ? '2px solid #1976d2' : 'none',
            color: activeTab === 'Graphs' ? '#1976d2' : 'grey',
          }}
        >
          Graphs
        </Button>
      </ButtonGroup>
      {activeTab === 'KPIs' && (
        <KPIsTab
          assetsItems={assetsItems}
          handleKpiCardClick={handleKpiCardClick}
          maintenanceData={maintenanceData}
          totalCostSaved={totalCostSaved}
          totalCosts={aggregateAssetData}
          totalDowntimeSaved={totalDowntimeSaved}
          totalProductivityImproved={totalProductivityImproved}
          totalTonnage={aggregateAssetData.totalTonnage}
          totalCurrentPmVsCmHrs={totalCurrentPmVsCmHrs}
          totalEngineHours={aggregateAssetData.totalEngineHours}
          totalMiles={aggregateAssetData.totalMiles}
          totalTonnageOfAllAssets={totalTonnageOfAllAssets}
        />
      )}
      {activeTab === 'Graphs' && (
        <GraphsTab
          maintenanceData={maintenanceData}
          totalCosts={aggregateAssetData}
          totalCurrentPmVsCmHrs={totalCurrentPmVsCmHrs}
          totalTonnageOfAllAssets={totalTonnageOfAllAssets}
        />
      )}
    </div>
  );
};

export default KPIsGraphs;
